import { ReactElement, ReactNode } from 'react'
import { NextPage } from 'next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { SWRConfig } from 'swr'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/next'
import { I18nextProvider } from 'react-i18next'

import { usePageview } from '@hooks/analytics'
import { ensureBuyLegacyPosthog } from '@lib/posthog'
import i18n from '@lib/i18n'
import { BugsnagContext } from '@contexts/bugsnag'
import { ApiError } from '@lib/api/errors'
import '@styles/globals.css'
import { GlobalFontSans } from '@styles/font'

type FormattedNextPage = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
}

type FormattedAppProps = AppProps & {
  Component: FormattedNextPage
}

export default function App({ Component, pageProps }: FormattedAppProps) {
  const getLayout = Component.getLayout || ((page) => page)

  ensureBuyLegacyPosthog()
  usePageview()

  return (
    <>
      <SWRConfig
        value={{
          revalidateOnMount: true,
          revalidateOnFocus: true,
          dedupingInterval: 60_000,
          onErrorRetry: (error, key, config, revalidate, context) => {
            if (
              error instanceof ApiError &&
              (error.status === 401 || error.status === 403)
            ) {
              return
            }
            if (context.retryCount >= 1) return
            setTimeout(() => revalidate({ retryCount: context.retryCount + 1 }), 1000)
          },
        }}
      >
        <I18nextProvider i18n={i18n}>
          <Head>
            <meta
              name='viewport'
              content='width=device-width, user-scalable=no'
            />
          </Head>
          <BugsnagContext>
            <GlobalFontSans />
            <main
              id='app-main'
              className='w-full h-full overflow-y-auto font-sans antialiased'
            >
              {getLayout(<Component {...pageProps} />)}
            </main>
          </BugsnagContext>
        </I18nextProvider>
      </SWRConfig>
      <Analytics />
      <SpeedInsights />
    </>
  )
}
